import { validVariableNameFromFilename } from '../services/common';
import { FSNode } from '../types';

type JoinFn = (...segments: string[]) => string;

const hasNodeProcess: boolean =
    typeof process !== 'undefined' && typeof process.versions === 'object' && !!process.versions?.node;

let joinImpl: JoinFn | null = null;

if (hasNodeProcess) {
    try {
        const nodePath: typeof import('path') = require('path');
        joinImpl = (...segments: string[]): string => nodePath.join(...segments.filter(Boolean));
    } catch (error: unknown) {
        joinImpl = null;
    }
}

const detectWindowsEnvironment = (): boolean => {
    if (hasNodeProcess) return process.platform === 'win32';
    if (typeof window !== 'undefined') {
        if (typeof window.platform?.getHostPlatform === 'function')
            return window.platform.getHostPlatform().toLowerCase() === 'win32';
        if (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string') {
            return /windows/i.test(navigator.userAgent);
        }
    }
    return false;
};

const preferBackslash: boolean = detectWindowsEnvironment();
const isTheiaBrowserOnlyEnvironment: boolean =
    typeof window !== 'undefined' && window?.LVGL_THEIA_BROWSER_ONLY === true;
const fallbackSeparator: string = preferBackslash && !isTheiaBrowserOnlyEnvironment ? '\\' : '/';

const normalizeSeparators = (value: string): string => value.replace(/[\\/]+/g, fallbackSeparator);

// Manual stand-in for Node's `path.join` when `joinImpl` is unavailable (e.g. a Theia/Electron
// renderer with no `process.versions.node`). Resolves "." and ".." segments the same way
// `path.join` does, rather than concatenating them verbatim - see path.test.ts for the exact
// semantics this must match (e.g. a lone "." folder joined onto a dir must collapse back to
// that dir, not `dir/.`, or callers comparing resolved paths by string equality get false
// negatives).
const joinFsPathSegmentsManually = (segments: string[]): string => {
    const normalizedSegments: string[] = segments.map(normalizeSeparators);
    const first: string = normalizedSegments[0];
    const isAbsolute: boolean = isAbsoluteFolderPath(first) || first.startsWith(fallbackSeparator);

    // A Windows drive root (e.g. "C:") must never be treated as a poppable segment like an
    // ordinary folder name, or a ".." above it removes the drive itself instead of clamping at
    // the root - see path.test.ts ("clamps '..' traversals at a Windows drive root").
    const driveRootMatch: RegExpMatchArray | null = isAbsolute ? first.match(/^[a-zA-Z]:(?=[\\/])/) : null;
    const rootPrefix: string = driveRootMatch ? driveRootMatch[0] : fallbackSeparator;

    const joined: string = normalizedSegments.join(fallbackSeparator);
    const withoutRoot: string = driveRootMatch ? joined.slice(driveRootMatch[0].length) : joined;
    const parts: string[] = withoutRoot.split(fallbackSeparator).filter(Boolean);

    const resolved: string[] = [];
    for (const part of parts) {
        if (part === '.') continue;
        if (part === '..') {
            if (resolved.length > 0 && resolved[resolved.length - 1] !== '..') {
                resolved.pop();
            } else if (!isAbsolute) {
                resolved.push('..');
            }
            continue;
        }
        resolved.push(part);
    }

    if (isAbsolute) {
        const separator: string = rootPrefix.endsWith(fallbackSeparator) ? '' : fallbackSeparator;
        return `${rootPrefix}${separator}${resolved.join(fallbackSeparator)}`;
    }
    return resolved.length > 0 ? resolved.join(fallbackSeparator) : '.';
};

export const joinFsPath = (...segments: (string | undefined | null)[]): string => {
    const filtered: string[] = segments.filter(
        (segment): segment is string => typeof segment === 'string' && segment.length > 0
    );

    if (filtered.length === 0) return '';

    if (joinImpl) return joinImpl(...filtered);

    return joinFsPathSegmentsManually(filtered);
};

/**
 * Normalizes a path to forward slashes.
 * Use this for UI/file matching where the platform may emit backslashes.
 */
export const normalizePath = (path: string): string => (path || '').replace(/\\/g, '/');

/**
 * Normalizes paths for equality checks and cache keys across Theia, browser, and native Windows forms.
 */
export const normalizeFilePathForComparison = (path: string): string => {
    const normalizedPath: string = normalizePath(path)
        .replace(/^\/+([A-Za-z]:\/)/, '$1')
        .replace(/^([A-Za-z]):/, (drive: string) => drive.toLowerCase());
    const withoutTrailingSlash: string = normalizedPath.replace(/\/+$/, '');
    return /^[a-z]:$/i.test(withoutTrailingSlash) ? `${withoutTrailingSlash}/` : withoutTrailingSlash;
};

/**
 * Returns the last path segment (after normalizing separators).
 */
export const basename = (path: string, withExtension: boolean = true): string => {
    const normalized: string = normalizePath(path);
    const parts: string[] = normalized.split('/').filter(Boolean);
    if (withExtension) return parts[parts.length - 1] ?? normalized;
    return parts[parts.length - 1]?.split('.').slice(0, -1).join('.') ?? normalized;
};

// Crude but simple way used throughout codebase to determine if a file is binary or text.
// NOTE: This is too prone to failure.
export const isBinaryFile = (path: string): boolean => /\.(ttf|png|jpg|jpeg|webp|bmp|gif|svg|wasm|bin)$/i.test(path);

export const createUniqueName = (name: string, names: ReadonlySet<string>): string => {
    let base: string = validVariableNameFromFilename(name).toLowerCase();
    if (!names.has(base)) return base;

    for (let i = 1; ; i++) {
        const newName: string = `${base}_${i}`;
        if (!names.has(newName)) return newName;
    }
};

export const ensureTrailingSlash = (path: string): string => (path.endsWith('/') ? path : `${path}/`);

export const getFileExtension = (path: string): string => {
    const normalized: string = normalizePath(path).toLowerCase();
    const index: number = normalized.lastIndexOf('.');
    return index >= 0 ? normalized.slice(index) : '';
};

// Normalizes the paths in the FSNode tree to use forward slashes
export const recursiveNormalizePathsToPosix = (nodes: FSNode[]): FSNode[] => {
    return nodes.map((node: FSNode) => {
        const normalizedPath: string = normalizePath(node.path);
        if (node.children) {
            return {
                ...node,
                path: normalizedPath,
                children: recursiveNormalizePathsToPosix(node.children)
            };
        } else {
            return {
                ...node,
                path: normalizedPath
            };
        }
    });
};

// Matches a Windows drive-letter path (e.g. "C:\" or "C:/"), a UNC path (e.g. "\\server"), or a
// POSIX absolute path (e.g. "/lib").
export const isAbsoluteFolderPath = (folder: string): boolean =>
    /^(?:[a-zA-Z]:[\\/]|[\\/]{2})/.test(folder) || folder.startsWith('/');

export const resolveToAbsolutePath = (path: string, projectPath: string | null): string => {
    const trimmed: string = path.trim();
    if (!trimmed || projectPath === null) return trimmed;

    if (isAbsoluteFolderPath(trimmed)) return trimmed;

    // Join relative path
    const separator: string = projectPath.includes('\\') ? '\\' : '/';
    return `${projectPath.replace(/[\\/]+$/, '')}${separator}${trimmed}`;
};
